import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Instagram, Quote } from 'lucide-react';

/* =========================================================
 *  VALMÉRA — UGC / Testimonials Section
 *  Inspired by:
 *    · 21st.dev @vaib215/stagger-testimonials  (staggered card deck)
 *    · 21st.dev @cult-ui/3d-carousel             (3-D rotation)
 * ======================================================= */

// ---- data: müşteri yorumları (UGC) ----------------------
const REVIEWS = [
  {
    id: 1,
    name: 'Elif A.',
    handle: '@elifa.style',
    city: 'İstanbul',
    product: 'Anadolu Nakşı · Büstiyer',
    rating: 5,
    text: '"Kumaşı ipek gibi akıyor, motifleri o kadar canlı ki elimden bırakamadım. Her bakışta yeni bir detay yakalıyorum. Valméra artık gardırobumun kalbi."',
    image: 'https://cdn.shopier.app/scaledoriginal/valmeraofficial_113ce90930191041e9875e70f4b0f5cd.jpeg',
  },
  {
    id: 2,
    name: 'Ayşe K.',
    handle: '@aysekaraca',
    city: 'İzmir',
    product: 'Yıldız Sarayı · Büstiyer',
    rating: 5,
    text: '"Edirne kırmızısı ile altın varağın buluşması efsane. Fotoğrafta gördüğümden bin kat güzel çıktı. Bir masal parçası taşıyor gibiyim."',
    image: 'https://cdn.shopier.app/scaledoriginal/valmeraofficial_9dc278c122d43453397a5118144109b6.jpeg',
  },
  {
    id: 3,
    name: 'Zeynep D.',
    handle: '@zeynepd_',
    city: 'Ankara',
    product: 'Nazende · Şal',
    rating: 5,
    text: '"Uzun ipek şalın boynuma dokunuşu inanılmaz zarif. Bir gecelik değil, bir hikâye satın aldım. Alan herkesin âşık olacağı bir tasarım."',
    image: 'https://cdn.shopier.app/scaledoriginal/valmeraofficial_bf025c20a3474d749a3328b041e5d152.JPG',
  },
  {
    id: 4,
    name: 'Selin M.',
    handle: '@selin.mkr',
    city: 'Bursa',
    product: 'Umay Twili · 108×13',
    rating: 5,
    text: '"Çantamın kulpuna bağladım, elimde çevirdim, saçıma taktım — her kombinde farklı bir dünya. Twilly aşkım artık ömür boyu Valméra."',
    image: 'https://cdn.shopier.app/scaledoriginal/valmeraofficial_2ccf1f6631bfae2863a0bd6a72986a67.JPG',
  },
  {
    id: 5,
    name: 'Deniz R.',
    handle: '@denizrgul',
    city: 'Antalya',
    product: 'Yabani Gül · Büstiyer',
    rating: 5,
    text: '"Kutusundan çıkan an bile bir törendi. Dikişler el işi, kenarlar kusursuz. Yaz akşamı denizin kenarında rüzgârda uçan bir güle döndüm."',
    image: 'https://cdn.shopier.app/scaledoriginal/valmeraofficial_04eccdf8b81121d66c7c76da8ae02baa.jpeg',
  },
  {
    id: 6,
    name: 'Ceren B.',
    handle: '@cerenbayrak',
    city: 'Eskişehir',
    product: 'Nergis · Büstiyer',
    rating: 5,
    text: '"Nergisin sarısı beni büyüledi. Cilt dostu ipek olduğu her dokunuşta belli oluyor. Anneme de aynısından aldım — kadın kadına Valméra."',
    image: 'https://cdn.shopier.app/scaledoriginal/valmeraofficial_81461ade773a7f2d5bd3e9f02da665b6.jpeg',
  },
  {
    id: 7,
    name: 'Merve Ş.',
    handle: '@mervesahin',
    city: 'İstanbul',
    product: 'Hibiskus · Pareo',
    rating: 5,
    text: '"Tatilin ilk gününden itibaren yanımdan ayırmadım. Pareo, elbise, şal — üç ürünü tek fiyata almış gibiyim. Hibiskus tam bir yaz aşkı."',
    image: 'https://cdn.shopier.app/scaledoriginal/valmeraofficial_254275ba58401e9ee45b00458ce87beb.png',
  },
  {
    id: 8,
    name: 'İpek Y.',
    handle: '@ipek.ykl',
    city: 'İstanbul',
    product: 'Kırlangıç & Puantiye',
    rating: 5,
    text: '"Küçük ama etkileyici bir hikâye. Kırlangıçları görünce anneannemin sandığındaki eşarplar geldi aklıma. Kalbime dokunan bir tasarım."',
    image: 'https://cdn.shopier.app/scaledoriginal/valmeraofficial_7c5b70cc619546f6e0a5bb6db01f35d8.jpeg',
  },
];

// ---- kart pozisyon fonksiyonu (deck / 3D) ---------------
// index: kartın konumu (0 = center, ±1 = yan, ±2 = arka, ...)
function getCardTransform(offset) {
  const abs = Math.abs(offset);
  const dir = Math.sign(offset);
  if (abs > 3) {
    return { opacity: 0, x: 0, y: 0, rotate: 0, scale: 0.6, zIndex: 0 };
  }
  const x = dir * (offset === 0 ? 0 : 90 * abs + 60);
  const y = 30 * abs;
  const rotate = dir * (offset === 0 ? 0 : 6 * abs);
  const scale = 1 - 0.09 * abs;
  const opacity = abs === 0 ? 1 : abs === 1 ? 0.85 : abs === 2 ? 0.55 : 0.25;
  const zIndex = 20 - abs;
  return { opacity, x, y, rotate, scale, zIndex };
}

// ---- Star helper ----------------------------------------
const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
    ))}
  </div>
);

// ---- Ana bileşen ----------------------------------------
const StaggerTestimonials = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = REVIEWS.length;

  // auto-rotate
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive((i) => (i + 1) % count), 5500);
    return () => clearInterval(id);
  }, [paused, count]);

  // klavye erişilebilirliği
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') setActive((i) => (i - 1 + count) % count);
      if (e.key === 'ArrowRight') setActive((i) => (i + 1) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count]);

  const items = useMemo(
    () =>
      REVIEWS.map((r, i) => {
        // dairesel mesafeyi hesapla (nearest offset)
        let off = i - active;
        if (off > count / 2) off -= count;
        if (off < -count / 2) off += count;
        return { r, off };
      }),
    [active, count]
  );

  const centerReview = REVIEWS[active];

  return (
    <section
      className="relative overflow-hidden py-24 md:py-32 bg-gradient-to-b from-[#faf7f2] via-[#f6efe6] to-[#efe6d8]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Dekoratif arka plan */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute -top-40 -left-32 w-[420px] h-[420px] bg-amber-300/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-32 w-[420px] h-[420px] bg-rose-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-black/5 rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-black/[0.03] rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        {/* başlık */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-8 bg-neutral-400" />
            <span className="text-[11px] tracking-[0.3em] text-neutral-500 uppercase">Sevgiyle Paylaşanlar</span>
            <span className="h-px w-8 bg-neutral-400" />
          </div>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-light tracking-wide text-neutral-900 mb-4"
            style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}
          >
            Müşterilerimizden
          </h2>
          <p className="text-neutral-600 max-w-xl mx-auto">
            Valmêra&apos;yı hayatına dokunan kadınların sözünden okuyun. Gerçek fotoğraflar, gerçek hikâyeler.
          </p>
        </div>

        {/* KART YIĞINI (Stagger / 3D) */}
        <div className="relative mx-auto" style={{ perspective: 1400 }}>
          <div className="relative h-[560px] md:h-[520px] flex items-center justify-center">
            {items.map(({ r, off }) => {
              const t = getCardTransform(off);
              const isCenter = off === 0;
              return (
                <motion.button
                  key={r.id}
                  onClick={() => setActive(REVIEWS.indexOf(r))}
                  aria-label={`${r.name}'nin yorumu`}
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 will-change-transform focus:outline-none"
                  style={{ zIndex: t.zIndex }}
                  animate={{
                    x: t.x,
                    y: t.y,
                    rotate: t.rotate,
                    scale: t.scale,
                    opacity: t.opacity,
                  }}
                  transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                >
                  <TestimonialCard review={r} isCenter={isCenter} />
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* NAV kontrolleri */}
        <div className="mt-10 md:mt-6 flex items-center justify-center gap-6">
          <button
            onClick={() => setActive((i) => (i - 1 + count) % count)}
            className="w-12 h-12 rounded-full bg-white border border-neutral-300 flex items-center justify-center hover:bg-neutral-900 hover:text-white transition-colors shadow-sm"
            aria-label="Önceki yorum"
          >
            <ChevronLeft size={18} />
          </button>

          {/* pagination noktaları */}
          <div className="flex items-center gap-2">
            {REVIEWS.map((r, i) => (
              <button
                key={r.id}
                onClick={() => setActive(i)}
                aria-label={`${i + 1}. yorum`}
                className={`h-1.5 rounded-full transition-all ${
                  i === active ? 'w-8 bg-neutral-900' : 'w-1.5 bg-neutral-400 hover:bg-neutral-600'
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => setActive((i) => (i + 1) % count)}
            className="w-12 h-12 rounded-full bg-white border border-neutral-300 flex items-center justify-center hover:bg-neutral-900 hover:text-white transition-colors shadow-sm"
            aria-label="Sonraki yorum"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* alt CTA */}
        <div className="text-center mt-14">
          <AnimatePresence mode="wait">
            <motion.p
              key={centerReview.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              className="text-sm text-neutral-500 tracking-wider"
            >
              <Instagram size={14} className="inline mr-2 -mt-0.5" />
              Sen de paylaş: <span className="text-neutral-900 font-medium">#Valmera</span> ·{' '}
              <span className="text-neutral-900 font-medium">@valmera_official</span>
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

// ---- Tek kart --------------------------------------------
const TestimonialCard = ({ review, isCenter }) => {
  return (
    <div
      className={`w-[300px] sm:w-[340px] md:w-[380px] bg-white rounded-2xl shadow-2xl overflow-hidden border border-neutral-200/60 ${
        isCenter ? 'ring-1 ring-neutral-900/5' : ''
      }`}
    >
      {/* görsel */}
      <div className="relative h-64 md:h-72 overflow-hidden bg-neutral-100">
        <img
          src={review.image}
          alt={`${review.name} — ${review.product}`}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/0 to-black/0" />
        {/* rozet */}
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full text-neutral-900 font-medium">
          Gerçek Müşteri
        </div>
        {/* handle */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white text-xs">
          <Instagram size={13} />
          <span className="opacity-90">{review.handle}</span>
        </div>
      </div>

      {/* içerik */}
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div
              className="text-base md:text-lg text-neutral-900 leading-tight"
              style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}
            >
              {review.name}
            </div>
            <div className="text-[11px] tracking-wider text-neutral-500 uppercase mt-0.5">
              {review.city}
            </div>
          </div>
          <Stars n={review.rating} />
        </div>

        <div className="relative">
          <Quote size={22} className="absolute -top-1 -left-1 text-amber-500/25" />
          <p className="text-[13px] md:text-sm text-neutral-700 leading-relaxed pl-4 italic">
            {review.text}
          </p>
        </div>

        <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center justify-between">
          <span className="text-[11px] tracking-wider text-neutral-500 uppercase">Satın aldığı ürün</span>
          <span className="text-[12px] font-medium text-neutral-900">{review.product}</span>
        </div>
      </div>
    </div>
  );
};

export default StaggerTestimonials;
